import { ChevronRight, Map } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { PaymentStatusCell } from "@/lib/dashboard/shared/PaymentStatusCell";
import { formatPlanDate } from "@/lib/builder/utils/payment-format";
import { useI18n } from "@/hooks/useI18n";
import { AgencyTripRow } from "@/integrations/supabase/rpc-types";
import {
  TripStateBadge,
  tripAdvisorName,
  tripClientName,
} from "@/lib/dashboard/trips/trip-display";

interface TripsTableProps {
  rows: AgencyTripRow[];
  /** Riga aperta nel pannello: resta evidenziata sotto lo sheet. */
  selectedId: string | null;
  onSelect: (row: AgencyTripRow) => void;
  showAdvisorColumn: boolean;
}

export function TripsTable({ rows, selectedId, onSelect, showAdvisorColumn }: TripsTableProps) {
  const { t } = useI18n();

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.overview.trips.columnTrip")}</TableHead>
                <TableHead>{t("dashboard.overview.trips.columnClient")}</TableHead>
                {showAdvisorColumn && (
                  <TableHead>{t("dashboard.overview.trips.columnAdvisor")}</TableHead>
                )}
                <TableHead>{t("dashboard.overview.trips.columnState")}</TableHead>
                <TableHead>{t("dashboard.overview.trips.columnDates")}</TableHead>
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1.5">
                    {t("dashboard.overview.paymentStatus.column")}
                    <InfoTooltip text={t("dashboard.overview.trips.paymentInfo")} />
                  </span>
                </TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const clientName = tripClientName(row);
                return (
                  <TableRow
                    key={row.itinerary_id}
                    onClick={() => onSelect(row)}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(row);
                      }
                    }}
                    className={`cursor-pointer ${
                      selectedId === row.itinerary_id ? "bg-orange-50/60" : ""
                    }`}
                  >
                    <TableCell className="font-medium text-gray-900">
                      <div className="flex flex-col gap-0.5">
                        <span>{row.title}</span>
                        <span className="text-xs text-gray-500 font-normal">
                          {row.destinations.join(", ") || "—"}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <UserAvatar
                        expanded
                        firstName={row.client_first_name}
                        lastName={row.client_last_name}
                        fullName={clientName}
                        email={row.client_email}
                        imageUrl={row.client_avatar_url}
                        size="sm"
                      />
                    </TableCell>

                    {showAdvisorColumn && (
                      <TableCell className="text-gray-600 whitespace-nowrap">
                        {tripAdvisorName(row) ?? "—"}
                      </TableCell>
                    )}

                    <TableCell>
                      <TripStateBadge state={row.trip_state} />
                    </TableCell>

                    <TableCell className="text-gray-600 whitespace-nowrap text-sm">
                      {row.start_date
                        ? `${formatPlanDate(row.start_date)}${
                            row.end_date ? ` – ${formatPlanDate(row.end_date)}` : ""
                          }`
                        : "—"}
                    </TableCell>

                    <TableCell className="text-right">
                      <PaymentStatusCell
                        compact
                        paidAmount={row.paid_amount}
                        pendingAmount={row.remaining_amount}
                        overdueAmount={row.overdue_amount}
                        totalAmount={row.plan_total}
                        empty={!row.has_plan}
                      />
                    </TableCell>

                    <TableCell className="text-right">
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/** Vuoto per davvero e vuoto per colpa dei filtri sono due messaggi diversi. */
export function TripsEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useI18n();

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
          <Map className="h-8 w-8 text-gray-300" />
          <p className="text-sm">
            {filtered
              ? t("dashboard.overview.trips.emptyFiltered")
              : t("dashboard.overview.trips.empty")}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default TripsTable;
